import { ACTION_TYPE } from "../core/redux/actionType";
import {
  AccessRuleEntitlementContext,
  AccessRuleMultiEntitlementContext,
} from "./accessRules/models";
import { AccessRuleMultiAddRequest } from "./accessRules/requests/accessRuleRequestBuilder";
import { bulkAddLiftAccessRulesAction } from "./bulkAddLiftAccessRulesAction";
import {
  countOutcomes,
  extractApiError,
  extractResultsArray,
  reconcileResults,
} from "./accessRules/liftAccessRuleSet/bulkResults";
import {
  LiftAccessRuleBatchSaveResult,
  LiftAccessRuleSaveResult,
} from "./models";

// TODO:  Make these global settings (Phase 2?)
const BULK_ADD_LIFT_ACCESS_RULES_BATCH_CONFIG = {
  entitlementTypeBatchSize: 25,
  contextBatchSize: 10,
  defaultConcurrency: 2,
  defaultConcurrencyDelayMs: 200,
  defaultBatchDelayMs: 100,
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const buildBatchedContexts = (
  contexts: AccessRuleMultiEntitlementContext[],
  entitlementBatchSize: number,
  contextBatchSize: number,
): AccessRuleMultiEntitlementContext[][] => {
  const batches: AccessRuleMultiEntitlementContext[][] = [];
  let currentBatch: AccessRuleMultiEntitlementContext[] = [];

  for (const ctx of contexts) {
    if (!ctx.EntitlementTypeIds?.length) continue;

    for (
      let i = 0;
      i < ctx.EntitlementTypeIds.length;
      i += entitlementBatchSize
    ) {
      const splitContext: AccessRuleMultiEntitlementContext = {
        ...ctx,
        EntitlementTypeIds: ctx.EntitlementTypeIds.slice(
          i,
          i + entitlementBatchSize,
        ),
      };

      currentBatch.push(splitContext);

      if (currentBatch.length >= contextBatchSize) {
        batches.push(currentBatch);
        currentBatch = [];
      }
    }
  }

  if (currentBatch.length > 0) {
    batches.push(currentBatch);
  }

  return batches;
};

/**
 * The rule sets a batch covers, one per entitlement type. The endpoint takes multi
 * contexts but answers per rule set, so this is what results reconcile against.
 */
export const flattenAddContexts = (
  contexts: AccessRuleMultiEntitlementContext[],
): AccessRuleEntitlementContext[] =>
  contexts.flatMap((ctx) =>
    (ctx.EntitlementTypeIds ?? []).map((entitlementTypeId) => ({
      EntitlementTypeId: entitlementTypeId,
      FulfillmentSeasonId: ctx.FulfillmentSeasonId,
      LiftAccessRuleScopeCategory: ctx.LiftAccessRuleScopeCategory,
      LiftAccessRuleScopeEntityId: ctx.LiftAccessRuleScopeEntityId,
    })),
  );

/**
 * Adds the rule sets in batches, reporting progress through bulkOperationProgress and
 * returning one result per rule set so the outcome screen can name what failed.
 *
 * Counts come from the reconciled results, not the API's own successCount: taking them
 * from the response lets the headline number and the grid below it disagree.
 */
export const bulkAddLiftAccessRulesBatched =
  (
    addRequest: AccessRuleMultiAddRequest,
    options?: { cancelRef?: { cancelled: boolean }; concurrency?: number },
  ) =>
  async (dispatch: any): Promise<LiftAccessRuleBatchSaveResult> => {
    const cancelRef = options?.cancelRef ?? { cancelled: false };
    const concurrency =
      options?.concurrency ??
      BULK_ADD_LIFT_ACCESS_RULES_BATCH_CONFIG.defaultConcurrency;

    const contextBatches = buildBatchedContexts(
      addRequest.Contexts,
      BULK_ADD_LIFT_ACCESS_RULES_BATCH_CONFIG.entitlementTypeBatchSize,
      BULK_ADD_LIFT_ACCESS_RULES_BATCH_CONFIG.contextBatchSize,
    );

    const allResults: LiftAccessRuleSaveResult[] = [];

    let processedEntitlementContexts = 0;
    let runningSuccessCount = 0;
    let runningFailureCount = 0;
    let stoppedReason: LiftAccessRuleBatchSaveResult["stoppedReason"];

    const totalEntitlementContexts = addRequest.Contexts.reduce(
      (sum, ctx) => sum + (ctx.EntitlementTypeIds?.length ?? 0),
      0,
    );

    dispatch({
      type: ACTION_TYPE.BULK_ADD_LIFT_ACCESS_RULES_BATCH_PROGRESS,
      payload: {
        current: 0,
        total: totalEntitlementContexts,
        successCount: 0,
        failureCount: 0,
      },
    });

    for (let i = 0; i < contextBatches.length; i += concurrency) {
      if (cancelRef.cancelled) break;

      const slice = contextBatches.slice(i, i + concurrency);

      const promises: Promise<any>[] = [];

      for (let k = 0; k < slice.length; k++) {
        if (k > 0) {
          await delay(BULK_ADD_LIFT_ACCESS_RULES_BATCH_CONFIG.defaultBatchDelayMs);
        }
        promises.push(
          dispatch(
            bulkAddLiftAccessRulesAction({
              ...addRequest,
              Contexts: slice[k],
            }),
          ),
        );
      }

      const dispatchedResults = await Promise.all(promises);

      // Per wave, written once: a later success must not erase an earlier failure.
      let firstOutrightFailure: LiftAccessRuleBatchSaveResult["stoppedReason"];

      for (let j = 0; j < dispatchedResults.length; j++) {
        const dispatched = dispatchedResults[j];
        const contexts = flattenAddContexts(slice[j]);

        // undefined = the middleware bailed out (breaker open), so nothing was sent.
        // Counting that as added would report rule sets created that were never touched.
        const wasSent = !!dispatched && typeof dispatched === "object";

        if (!wasSent) {
          if (!firstOutrightFailure) firstOutrightFailure = { notSent: true };
          continue;
        }

        let perContext = extractResultsArray(dispatched);

        // A 2xx with no results array fails this batch only: the request was
        // processed, so its rule sets count as unanswered rather than as added.
        if (!perContext && !dispatched.error) {
          console.warn(
            `Bulk add batch ${i + j + 1}: response carried no results array`,
          );
          perContext = [];
        }

        // Nothing in the batch was examined, so its rule sets are unattempted rather
        // than individually failed. Never infer success from an empty body.
        if (!perContext) {
          if (!firstOutrightFailure)
            firstOutrightFailure = extractApiError(dispatched);
          continue;
        }

        const batchResults = reconcileResults(contexts, perContext);
        const counts = countOutcomes(batchResults);

        allResults.push(...batchResults);
        runningSuccessCount += counts.successCount;
        runningFailureCount += counts.failureCount;
        processedEntitlementContexts += contexts.length;

        dispatch({
          type: ACTION_TYPE.BULK_ADD_LIFT_ACCESS_RULES_BATCH_PROGRESS,
          payload: {
            current: processedEntitlementContexts,
            total: totalEntitlementContexts,
            successCount: runningSuccessCount,
            failureCount: runningFailureCount,
          },
        });
      }

      // Later batches fail the same way, so stop. The remainder counts as not
      // attempted and the outcome screen offers it to Retry.
      if (firstOutrightFailure) {
        stoppedReason = firstOutrightFailure;
        console.warn(
          "Bulk add stopped: request failed before any rule set was processed",
          stoppedReason,
        );
        break;
      }

      if (!cancelRef.cancelled) {
        await delay(
          BULK_ADD_LIFT_ACCESS_RULES_BATCH_CONFIG.defaultConcurrencyDelayMs,
        );
      }
    }

    const combinedResult: LiftAccessRuleBatchSaveResult = {
      results: allResults,
      successCount: runningSuccessCount,
      failureCount: runningFailureCount,
      hasFailures: runningFailureCount > 0,
      cancelled: cancelRef.cancelled,
      // A batch that failed as a whole never advanced `processed`, so its rule sets
      // land here rather than in failureCount.
      notAttempted: Math.max(
        totalEntitlementContexts - processedEntitlementContexts,
        0,
      ),
      stoppedReason,
      runUtcDateTime: addRequest.RequestUtcDateTime,
    };

    dispatch({
      type: ACTION_TYPE.BULK_ADD_LIFT_ACCESS_RULES_BATCH_COMPLETE,
      payload: combinedResult,
    });

    return combinedResult;
  };
